import { randomId } from "@mui/x-data-grid-generator";
import {
  DisplayUnitsDataGridRow,
  UnitInfo,
  ROUND_DIGIT_DEFAULT,
  ROUND_DIGIT_MIN,
  ROUND_DIGIT_MAX,
  RoundMethod,
  ROUND_METHOD_DEFAULT,
} from "./UnitPickerInterfaces";
import { PropertyValueAttribute } from "store/reducers/products/parameters/pl-parameters.types";
import {
  DataTypeEnum,
  DomainEnum,
  PropertyResponse,
  Property,
  PropertyValue,
} from "store/reducers/products/properties/pl-properties.types";
import ecatUnits from "./ecatUnits.json";
import { ParentPropertyContextType } from "../../pages/ProductDetail/ProductLineProperties/ParentPropertyContext";

export const BuildSubproperty = ({
  parentPropertyContext,
  subPropertyName,
  attributeNames,
  propertyValueAttributes,
  getPropValues,
  comments,
}: {
  parentPropertyContext: ParentPropertyContextType;
  subPropertyName: string;
  attributeNames: string[];
  propertyValueAttributes: PropertyValueAttribute[];
  getPropValues: (
    attrs: Record<string, PropertyValueAttribute>
  ) => PropertyValue[];
  comments?: string;
}): Property => {
  // 1. Ensure required context is present
  if (!parentPropertyContext) {
    throw new Error("parentPropertyContext is required but was not provided.");
  }

  // 2. Collect ruleset IDs and tag IDs
  const rulesetIds: string[] =
    parentPropertyContext.parentPropertyRulesets?.map(
      (ruleSet) => ruleSet.id
    ) ?? [];

  const tagIds: string[] =
    parentPropertyContext.parentPropertyTags
      ?.filter((tag) => tag.category === "UI_LOCATION")
      .map((tag) => tag.id) ?? [];

  // 3. Lookup the required PropertyValueAttributes by name
  const attributeMap: Record<string, PropertyValueAttribute> =
    attributeNames.reduce((acc, attrName) => {
      const found = propertyValueAttributes.find(
        (pva) => pva.name === attrName
      );
      if (!found) {
        throw new Error(
          `PropertyValueAttribute with name "${attrName}" not found.`
        );
      }
      acc[attrName] = found;
      return acc;
    }, {} as Record<string, PropertyValueAttribute>);

  // 4. Delegate building of propertyValues to the caller
  const propertyValues: PropertyValue[] = getPropValues(attributeMap);

  // 5. Construct and return the PropertyDB object
  return {
    productlineId: parentPropertyContext.productLineId,
    parentId: parentPropertyContext.parentId,
    name: parentPropertyContext.parentPropertyName + subPropertyName,
    dataType: DataTypeEnum.STRING,
    domain: DomainEnum.DISCRETE,
    default: null,
    rulesetIds,
    tagIds,
    propertyValueAttributeIds: Object.values(attributeMap).map(
      (attr) => attr.id
    ),
    propertyValues,
    comments: comments ?? "Generated via macro",
    sources: "",
    autoSelection: false,
    ownerTool: "",
  };
};

export const CreateDisplayUnitsDataGridRow = (
  displayUnit: string,
  isDefault = false,
  roundMethod: RoundMethod = ROUND_METHOD_DEFAULT,
  roundDigits: number | null = ROUND_DIGIT_DEFAULT
): DisplayUnitsDataGridRow => {
  const row: DisplayUnitsDataGridRow = {
    id: randomId(),
    displayUnit: displayUnit,
    isDefault: isDefault,
    roundMethod: roundMethod,
    roundDigit: roundDigits,
    __reorder__: "drag row to reorder",
  };

  return row;
};

//Build a set of all units from our ecatUnits json that lists
//unit name and the unit group (i.e. "Temperature")
//This will be used for the options list
export const GetAllUnits = (): UnitInfo[] => {
  const unitsWithGroups: UnitInfo[] = [];

  ecatUnits.unitGroups.forEach((group) => {
    group.units.forEach((unit) => {
      unitsWithGroups.push({
        label: unit,
        group: group.group,
      });
    });
  });

  return unitsWithGroups;
};

export const GetUnitGroupFromUnit = (
  unit: string,
  allUnitInfo?: UnitInfo[]
): string => {
  if (!allUnitInfo) allUnitInfo = GetAllUnits();

  const unitInfo: UnitInfo | undefined = allUnitInfo.find(
    (ui) => ui.label === unit
  );
  return unitInfo?.group ? unitInfo.group : "";
};

export const GetGroupUnits = (
  group = "",
  allUnitInfo?: UnitInfo[]
): string[] => {
  if (!allUnitInfo) allUnitInfo = GetAllUnits();
  if (!group) return allUnitInfo.map((unit) => unit.label);

  const unitsForActiveGroup: string[] = allUnitInfo
    .filter((unit) => unit.group === group)
    .map((unit) => {
      return unit.label;
    });
  return unitsForActiveGroup;
};

export const GetPropertyValues = (
  property: PropertyResponse
): string[] | null => {
  if (!property) return null;
  if (property.domain !== DomainEnum.DISCRETE) return null;
  if (!property.propertyValues) return null;

  //Get the property values for the property
  const values: string[] = property?.propertyValues.map((propertyValue) => {
    const cellValue = propertyValue?.valueAttributeCells.find((vac) => {
      if (vac.propertyValueAttribute.name === "Value") return vac;
    });
    return cellValue?.value ?? "";
  });
  return values;
};

export const ValidateRoundDigits = (
  roundDigits: string | number | null | undefined
): number | null => {
  //if null or undefined set to null
  if (roundDigits === null || roundDigits === undefined) return null;

  //If no value, return the default
  if (!roundDigits) return ROUND_DIGIT_DEFAULT;

  const numberRoundDigits = Number(roundDigits);
  if (Number.isNaN(numberRoundDigits)) return ROUND_DIGIT_DEFAULT;

  //If less than minimum, return minimum
  if (numberRoundDigits < ROUND_DIGIT_MIN) return ROUND_DIGIT_MIN;

  //If more than max, return max
  if (numberRoundDigits > ROUND_DIGIT_MAX) return ROUND_DIGIT_MAX;

  //Return the supplied value
  return numberRoundDigits;
};

const validRoundMethods = new Set<string>(
  Object.values(RoundMethod) as string[]
);

export function ValidateRoundMethod(
  method: string | null | undefined
): RoundMethod {
  // null or unknown? Use the default
  if (!method) {
    return ROUND_METHOD_DEFAULT;
  }

  // Check membership in our set, then safely cast
  return validRoundMethods.has(method)
    ? (method as RoundMethod)
    : ROUND_METHOD_DEFAULT;
}
